<template>
    <AdminLayout>
        <Head title="Gesendete Erinnerungen" />

        <div class="py-12">
            <div class="max-w-7xl mx-auto sm:px-6 lg:px-8">
                <div class="bg-white overflow-hidden shadow-sm sm:rounded-lg">
                    <div class="p-6">
                        <h2 class="text-2xl font-semibold mb-6">Gesendete Erinnerungen</h2>

                        <!-- Search Bar -->
                        <div class="mb-4">
                            <InputText
                                v-model="searchQuery"
                                @input="debouncedSearch"
                                placeholder="Suche nach Kunde oder E-Mail..."
                                class="w-full"
                            />
                        </div>

                        <!-- Data Table -->
                        <DataTable
                            :value="alerts.data"
                            stripedRows
                            @row-click="openDetailModal"
                            class="cursor-pointer"
                        >
                            <Column field="customer_name" header="Kunde" style="min-width: 150px"></Column>
                            <Column field="title_preview" header="Titel" style="min-width: 200px"></Column>
                            <Column field="body_preview" header="Nachricht" style="min-width: 250px"></Column>
                            <Column field="created_at" header="Gesendet" style="min-width: 150px"></Column>
                            <template #empty>
                                <div class="text-center py-6 text-gray-500">
                                    Keine Erinnerungen gefunden.
                                </div>
                            </template>
                        </DataTable>

                        <!-- Pagination -->
                        <div class="mt-6 flex justify-center items-center gap-2">
                            <Button
                                icon="pi pi-chevron-left"
                                :disabled="!alerts.prev_page_url"
                                @click="goToPage(alerts.prev_page_url)"
                                text
                                rounded
                            />
                            <template v-for="(page, index) in paginationPages" :key="index">
                                <span v-if="page === '...'" class="px-3 py-2 text-gray-500">...</span>
                                <Button
                                    v-else
                                    :label="String(page)"
                                    @click="goToPageNumber(page)"
                                    :severity="alerts.current_page === page ? 'primary' : 'secondary'"
                                    :text="alerts.current_page !== page"
                                    rounded
                                />
                            </template>
                            <Button
                                icon="pi pi-chevron-right"
                                :disabled="!alerts.next_page_url"
                                @click="goToPage(alerts.next_page_url)"
                                text
                                rounded
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Detail Modal -->
        <Dialog
            v-model:visible="showDetailModal"
            header="Erinnerung Details"
            :modal="true"
            :style="{ width: '600px' }"
        >
            <div v-if="selectedAlert" class="space-y-4">
                <div>
                    <strong>Kunde:</strong> {{ selectedAlert.customer_full_name }}
                </div>
                <div>
                    <strong>E-Mail:</strong> {{ selectedAlert.customer_email }}
                </div>
                <div>
                    <strong>Gesendet:</strong> {{ selectedAlert.created_at }}
                </div>
                <div>
                    <strong>Titel:</strong>
                    <div class="mt-2">{{ selectedAlert.title }}</div>
                </div>
                <div>
                    <strong>Nachricht:</strong>
                    <div class="mt-2 whitespace-pre-wrap">{{ selectedAlert.body }}</div>
                </div>
            </div>

            <template #footer>
                <Button label="Schließen" @click="showDetailModal = false" />
            </template>
        </Dialog>
    </AdminLayout>
</template>

<script setup>
import { ref, computed } from 'vue';
import AdminLayout from '../../../../Layouts/AdminLayout.vue';
import { Head } from '@inertiajs/inertia-vue3';
import { Inertia } from '@inertiajs/inertia';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';

const props = defineProps({
    alerts: Object,
    filters: Object,
});

const searchQuery = ref(props.filters.search || '');
const showDetailModal = ref(false);
const selectedAlert = ref(null);

// Generate pagination with ellipsis
const paginationPages = computed(() => {
    const current = props.alerts.current_page;
    const last = props.alerts.last_page;
    const pages = [];

    if (last <= 7) {
        // Show all pages if 7 or fewer
        for (let i = 1; i <= last; i++) {
            pages.push(i);
        }
    } else {
        // Always show first page
        pages.push(1);

        if (current <= 3) {
            // Near the beginning
            pages.push(2, 3, 4, '...', last);
        } else if (current >= last - 2) {
            // Near the end
            pages.push('...', last - 3, last - 2, last - 1, last);
        } else {
            // In the middle
            pages.push('...', current - 1, current, current + 1, '...', last);
        }
    }

    return pages;
});

let debounceTimer;
const debouncedSearch = () => {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
        Inertia.get('/admin/alerts/sent', { search: searchQuery.value }, {
            preserveState: true,
            preserveScroll: true,
        });
    }, 300);
};

const goToPage = (url) => {
    if (url) {
        Inertia.get(url, {}, {
            preserveState: true,
            preserveScroll: true,
        });
    }
};

const goToPageNumber = (page) => {
    Inertia.get('/admin/alerts/sent', {
        page: page,
        search: searchQuery.value
    }, {
        preserveState: true,
        preserveScroll: true,
    });
};

const openDetailModal = (event) => {
    selectedAlert.value = event.data;
    showDetailModal.value = true;
};
</script>
